import { useCallback } from 'react';

import { componentLogger } from '@ragu-components/logger';
import { HttpError, useTranslate } from 'react-admin';

import { useIngordDataProvider } from 'src/components/flowchart/hooks/useIngordDataProvider';

interface OperationResult {
  successful: string[];
  failed: string[];
  errors: Set<string>;
}

interface UseUserRecordingConfsOperationsReturn {
  createAssignments: (userIds: string[], type: string) => Promise<OperationResult>;
  deleteAssignments: (assignmentIds: string[]) => Promise<OperationResult>;
}

export const useUserRecordingConfsOperations = (confId: string): UseUserRecordingConfsOperationsReturn => {
  const dataProvider = useIngordDataProvider();
  const translate = useTranslate();
  const resource = `call_recording_confs/${confId}/user_call_recording_confs`;

  const addError = useCallback(
    (error: unknown, errors: Set<string>): void => {
      if (error instanceof HttpError) {
        errors.add(translate(error.message, { _: error.message }));
      }
    },
    [translate]
  );

  const createAssignments = useCallback(
    async (userIds: string[], type: string): Promise<OperationResult> => {
      const successful: string[] = [];
      const failed: string[] = [];
      const errors = new Set<string>();

      for (const userId of userIds) {
        try {
          await dataProvider.create(resource, { data: { user_id: userId, type } });
          successful.push(userId);
        } catch (error) {
          failed.push(userId);
          componentLogger.error('Error creating user recording conf assignment', error);
          addError(error, errors);
        }
      }

      return { successful, failed, errors };
    },
    [dataProvider, resource, addError]
  );

  const deleteAssignments = useCallback(
    async (assignmentIds: string[]): Promise<OperationResult> => {
      const successful: string[] = [];
      const failed: string[] = [];
      const errors = new Set<string>();

      for (const id of assignmentIds) {
        try {
          await dataProvider.delete(resource, { id });
          successful.push(id);
        } catch (error) {
          failed.push(id);
          componentLogger.error('Error deleting user recording conf assignment', error);
          addError(error, errors);
        }
      }

      return { successful, failed, errors };
    },
    [dataProvider, resource, addError]
  );

  return { createAssignments, deleteAssignments };
};
